import React, { useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useReducedMotion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faClone,
	faMagnet,
	faMinus,
	faPlus,
	faRotateLeft,
	faTrashCan,
	faXmark,
} from "@fortawesome/free-solid-svg-icons"
import SegmentedPill from "../../atoms/SegmentedPill"
import { useBackClose } from "../../atoms/useBackClose"
import { theme } from "../../settings/config"
import {
	FIELD,
	MAX_NUMBER,
	MAX_SCHEMA_ITEMS,
	PLAYER_COLORS,
	ROUND_BEND,
	arrowLength,
	arrowPoint,
	bendThrough,
	duplicateItem,
	keepOnField,
	newPiece,
	normalizeSchema,
	schemaView,
	snapPoint,
	snapTargets,
} from "../../utils/exerciseSchema.utils"
import {
	pieceLift,
	pieceScale,
	PieceGlyph,
	SchemaArrow,
	SchemaCourt,
	SchemaPiece,
	SchemaZone,
	arrowScreenPoints,
	pathOf,
	piecesByDepth,
	polygonPoints,
	zoneCorners,
} from "./SchemaDrawing"
import { FULL_SCREEN, between, drawnRect, flipFrom, hasSize, insetOf, play } from "./schemaMotion"

// L'editor dello schema di un esercizio (#167): il campo, e sotto i pezzi
// da metterci sopra. Si tocca un pezzo e va in campo, poi lo si trascina;
// una freccia si tira sul campo da dove parte a dove arriva, e il pallino
// a metà la piega.
//
// Si compone in prospettiva, come le schede, o dall'alto, dove in fondo al
// campo i pezzi non rimpiccioliscono: lo schema è lo stesso, cambia solo
// come lo si guarda. La vista scelta l'ultima volta la si ritrova.
//
// Sta sopra a tutto, a schermo intero: il foglio del programma da cui si
// apre è già un modal, e il campo ha bisogno di tutto lo spazio che c'è.
// "Fatto" lascia lo schema all'esercizio; si salva col programma.
//
// Si apre crescendo da dov'era lo schema (`origin`, se chi lo apre lo
// sa): il campo parte da lì e arriva al suo posto, il blu si allarga con
// lui fino a coprire lo schermo, e i pezzi salgono da sotto. Chiudendo, il
// campo torna dov'era. Senza `origin`, il campo compare dov'è.
//
// Trascinando, un pezzo o la punta di una freccia si aggancia alle file
// degli altri e alle righe del campo (`snapPoint`), e la fila si vede: una
// riga arancione tratteggiata da un capo all'altro, e gli anelli su chi è
// in fila. Nella barra, mentre si trascina, "Guide / Libero" lo spegne, e
// l'app se lo ricorda. Toccato un pezzo o una freccia, sopra di lui c'è
// "Duplica": sul campo e non nella barra, che per un giocatore è già piena
// dei suoi colori.

const c = theme.colors

const VIEW_KEY = "incampo.schemaView"
const readView = () => {
	try {
		return window.localStorage.getItem(VIEW_KEY) === "top" ? "top" : "persp"
	} catch {
		return "persp"
	}
}
const storeView = mode => {
	try {
		window.localStorage.setItem(VIEW_KEY, mode)
	} catch {
		// navigazione privata: la vista non si ricorda, pazienza
	}
}

// Le guide che agganciano: accese, finché non le si spegne
const SNAP_KEY = "incampo.schemaSnap"
const readSnap = () => {
	try {
		return window.localStorage.getItem(SNAP_KEY) !== "off"
	} catch {
		return true
	}
}
const storeSnap = on => {
	try {
		window.localStorage.setItem(SNAP_KEY, on ? "on" : "off")
	} catch {
		// vale finché l'editor è aperto
	}
}

// Nell'editor ogni pezzo ha un nome suo, per seguirlo col dito; salvato no
let lastID = 0
const withID = item => ({ ...item, id: `s${++lastID}` })

// Un tasto piccolo della barra sopra i pezzi
const barButton = (pressed = false) => ({
	height: "2rem",
	padding: "0 0.625rem",
	border: 0,
	borderRadius: "15px",
	background: pressed ? c.gunmetal_fill : c.surface,
	color: pressed ? c.white : c.gunmetal,
	font: "inherit",
	fontSize: "0.75rem",
	fontWeight: 600,
	display: "inline-flex",
	alignItems: "center",
	gap: "0.375rem",
	flex: "none",
	cursor: "pointer",
})

const iconButton = {
	width: "2.25rem",
	height: "2.25rem",
	border: 0,
	padding: 0,
	background: "transparent",
	color: c.text_light,
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	flex: "none",
	cursor: "pointer",
}

// Le icone dei pezzi nel vassoio: gli stessi pezzi del campo
const TrayIcon = ({ children, viewBox }) => (
	<svg viewBox={viewBox} width="30" height="26" aria-hidden="true" style={{ overflow: "visible" }}>
		{children}
	</svg>
)

const TRAY = [
	{
		add: "player",
		label: "Giocatore",
		icon: (
			<TrayIcon viewBox="-16 -32 32 36">
				<PieceGlyph item={{ type: "player", color: "red" }} />
			</TrayIcon>
		),
	},
	{
		add: "coach",
		label: "Maestro",
		icon: (
			<TrayIcon viewBox="-16 -26 32 30">
				<PieceGlyph item={{ type: "coach" }} />
			</TrayIcon>
		),
	},
	{
		add: "cone",
		label: "Coni/coppelle",
		icon: (
			<TrayIcon viewBox="-18 -27 36 31">
				<g transform="translate(-7,0) scale(0.95)">
					<PieceGlyph item={{ type: "cone", tall: true }} />
				</g>
				<g transform="translate(7,1) scale(0.75)">
					<PieceGlyph item={{ type: "cone" }} />
				</g>
			</TrayIcon>
		),
	},
	{
		add: "zone",
		label: "Zona",
		icon: (
			<TrayIcon viewBox="0 0 30 26">
				<rect x="3" y="5" width="24" height="16" rx="1" fill={c.schema_inside} />
				<rect x="9" y="8" width="14" height="10" fill={c.schema_zone} stroke={c.schema_zone_line} strokeWidth="1.2" />
			</TrayIcon>
		),
	},
	{
		tool: "tesa",
		label: "Tesa",
		icon: (
			<TrayIcon viewBox="0 0 30 26">
				<path d="M4 21 L21 8" stroke="currentColor" strokeWidth="2.4" strokeDasharray="4 3" strokeLinecap="round" fill="none" />
				<polygon points="27,4 18,6 23,12" fill="currentColor" />
			</TrayIcon>
		),
	},
	{
		tool: "tonda",
		label: "Tonda",
		icon: (
			<TrayIcon viewBox="0 0 30 26">
				<path d="M3 22 Q4 6 20 6" stroke="currentColor" strokeWidth="2.4" strokeDasharray="4 3" strokeLinecap="round" fill="none" />
				<polygon points="28,6 20,1.5 20,10.5" fill="currentColor" />
			</TrayIcon>
		),
	},
	{
		tool: "spostamento",
		label: "Spostamento",
		icon: (
			<TrayIcon viewBox="0 0 30 26">
				<path d="M4 13 L24 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
				<polygon points="28,13 21,8.5 21,17.5" fill="currentColor" />
			</TrayIcon>
		),
	},
	{
		add: "number",
		label: "Numero",
		icon: (
			<TrayIcon viewBox="-16 -20 32 24">
				<PieceGlyph item={{ type: "number", n: 1 }} />
			</TrayIcon>
		),
	},
]

const PIECE_NAMES = { coach: "Maestro", zone: "Zona: il pallino nell'angolo la allarga" }

// Com'è l'editor aperto; da chiuso lo dice `closedState`
const OPEN = {
	bg: { clipPath: FULL_SCREEN, opacity: 1 },
	court: { x: 0, y: 0, scale: 1, opacity: 1 },
	top: { y: 0, opacity: 1 },
	tray: { y: 0 },
}

// Aprendo, la testata arriva dopo il campo e il foglio dei pezzi finisce
// per ultimo; chiudendo se ne vanno prima loro
const OPENING = { top: { delay: 0.12, duration: 0.3 }, tray: { delay: 0.06, duration: 0.4 } }
const CLOSING = { top: { duration: 0.18 }, tray: { duration: 0.3 } }

function SchemaEditor({ schema, title, origin, onDone, onCancel }) {
	const [items, setItems] = useState(() => (normalizeSchema(schema)?.items ?? []).map(withID))
	const [history, setHistory] = useState([])
	const [selectedID, setSelectedID] = useState(null)
	const [tool, setTool] = useState(null)
	const [mode, setModeState] = useState(readView)
	const [snapOn, setSnapState] = useState(readSnap)
	// mentre il dito trascina: la barra dice dell'aggancio, e "Duplica" si
	// toglie di mezzo; `guides` sono le file su cui sta quello che si sposta
	const [dragging, setDragging] = useState(false)
	const [guides, setGuides] = useState(null)
	const svgRef = useRef(null)
	const drag = useRef(null)
	const reduceMotion = useReducedMotion()
	const parts = { root: useRef(null), bg: useRef(null), court: useRef(null), top: useRef(null), tray: useRef(null) }
	const motion = useRef(null)
	const leaving = useRef(false)

	const view = useMemo(() => schemaView(mode, 560), [mode])
	const selected = items.find(item => item.id === selectedID)
	const isFull = items.length >= MAX_SCHEMA_ITEMS

	const setMode = next => {
		setModeState(next)
		storeView(next)
	}

	const setSnap = on => {
		setSnapState(on)
		storeSnap(on)
	}

	const remember = (before = items) => setHistory(past => [...past.slice(-59), before])

	const undo = () => {
		if (history.length === 0) return
		const previous = history[history.length - 1]
		setHistory(past => past.slice(0, -1))
		setItems(previous)
		if (!previous.some(item => item.id === selectedID)) setSelectedID(null)
	}

	const change = (id, patch) =>
		setItems(current => current.map(item => (item.id === id ? { ...item, ...patch } : item)))

	const editSelected = patch => {
		remember()
		change(selectedID, patch)
	}

	const add = type => {
		if (isFull) return
		setTool(null)
		remember()
		const piece = withID(newPiece(items, type))
		setItems([...items, piece])
		setSelectedID(piece.id)
	}

	const remove = () => {
		remember()
		setItems(items.filter(item => item.id !== selectedID))
		setSelectedID(null)
	}

	// la copia arriva un metro più in là, già scelta, pronta da spostare
	const duplicate = () => {
		if (!selected || isFull) return
		remember()
		const copy = withID(duplicateItem(items, selected))
		setItems([...items, copy])
		setSelectedID(copy.id)
	}

	// Dal dito al campo: in metri, dentro il verde
	const courtPoint = event => {
		const svg = svgRef.current
		const matrix = svg?.getScreenCTM?.()
		if (!matrix) return null
		const p = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse())
		return keepOnField(view.toCourt(p.x, p.y))
	}

	const onPointerDown = event => {
		const at = courtPoint(event)
		if (!at) return
		event.preventDefault()
		svgRef.current.setPointerCapture?.(event.pointerId)
		const target = event.target.closest?.("[data-id]")
		const part = target?.dataset.part ?? "body"

		// con una freccia scelta, si disegna anche partendo da un pezzo:
		// è da lì che di solito parte la palla
		if (tool && !(target && part !== "body")) {
			if (isFull) return
			remember()
			const targets = snapTargets(items)
			const start = snapOn ? keepOnField(snapPoint(at, targets)) : at
			const arrow = withID({
				type: "arrow",
				kind: tool === "spostamento" ? "spostamento" : "palla",
				a: start,
				b: start,
				bend: tool === "tonda" ? -ROUND_BEND : 0,
				both: false,
			})
			setItems([...items, arrow])
			setSelectedID(arrow.id)
			setDragging(true)
			drag.current = { id: arrow.id, part: "b", fresh: true, moved: true, before: items, targets }
			return
		}
		if (!target) {
			setSelectedID(null)
			drag.current = null
			return
		}
		const item = items.find(other => other.id === target.dataset.id)
		setSelectedID(item.id)
		drag.current = {
			id: item.id,
			part,
			start: at,
			original: item,
			moved: false,
			before: items,
			targets: snapTargets(items, item.id),
		}
	}

	const onPointerMove = event => {
		const d = drag.current
		if (!d) return
		const at = courtPoint(event)
		if (!at) return
		if (!d.moved) {
			// un tocco che trema non è uno spostamento
			if (Math.hypot(at.x - d.start.x, at.y - d.start.y) < 0.15) return
			remember(d.before)
			d.moved = true
			setDragging(true)
		}
		const o = d.original
		const dx = d.start ? at.x - d.start.x : 0
		const dy = d.start ? at.y - d.start.y : 0
		// i pezzi e le punte delle frecce si agganciano; una freccia intera o
		// una zona si spostano e basta
		const isEnd = d.part === "a" || d.part === "b"
		const isPiece = o ? o.type !== "arrow" && o.type !== "zone" : false
		const wanted = isEnd ? at : isPiece ? keepOnField({ x: o.x + dx, y: o.y + dy }) : null
		const snapped = wanted && snapOn ? snapPoint(wanted, d.targets) : null
		const to = snapped ? keepOnField(snapped) : wanted
		const inLine = snapped && (snapped.guides.x !== null || snapped.guides.y !== null)
		setGuides(inLine ? { ...snapped.guides, with: snapped.with, at: to } : null)
		setItems(current =>
			current.map(item => {
				if (item.id !== d.id) return item
				if (item.type === "arrow") {
					if (d.part === "a") return { ...item, a: to }
					if (d.part === "b") return { ...item, b: to }
					if (d.part === "bend") return { ...item, bend: bendThrough(item, at) }
					return {
						...item,
						a: keepOnField({ x: o.a.x + dx, y: o.a.y + dy }),
						b: keepOnField({ x: o.b.x + dx, y: o.b.y + dy }),
					}
				}
				if (item.type === "zone") {
					if (d.part === "corner") return { ...item, x2: at.x, y2: at.y }
					const from = keepOnField({ x: o.x1 + dx, y: o.y1 + dy })
					const to = keepOnField({ x: o.x2 + dx, y: o.y2 + dy })
					return { ...item, x1: from.x, y1: from.y, x2: to.x, y2: to.y }
				}
				return { ...item, ...to }
			}),
		)
	}

	const onPointerUp = () => {
		const d = drag.current
		drag.current = null
		setDragging(false)
		setGuides(null)
		if (!d?.fresh) return
		const arrow = items.find(item => item.id === d.id)
		// un tocco senza trascinare non è una freccia: si resta pronti a tirarla
		if (arrow && arrowLength(arrow) < 1) {
			setItems(d.before)
			setHistory(past => past.slice(0, -1))
			setSelectedID(null)
		} else setTool(null)
	}

	// Dove stanno le cose da chiuso: il campo sopra quello da cui si è
	// aperto, il blu ritagliato su di lui. Senza misure (chi preferisce meno
	// movimento, o il telefono finto dei test) niente animazione.
	const closedState = () => {
		const svgBox = svgRef.current?.getBoundingClientRect()
		if (reduceMotion || !hasSize(svgBox)) return null
		const from = origin?.()
		return {
			bg: hasSize(from?.area)
				? { clipPath: insetOf(from.area, from.radius), opacity: 1 }
				: { clipPath: FULL_SCREEN, opacity: 0 },
			court: hasSize(from?.court)
				? { ...flipFrom(from.court, drawnRect(svgBox, view.width, view.height)), opacity: 1 }
				: { x: 0, y: 0, scale: 0.94, opacity: 0 },
			top: { y: -12, opacity: 0 },
			tray: { y: parts.tray.current?.offsetHeight ?? 0 },
		}
	}

	const run = (from, to, enter) => {
		const els = Object.fromEntries(Object.entries(parts).map(([name, ref]) => [name, ref.current]))
		return play(between(els, from, to, { enter, timing: enter ? OPENING : CLOSING }), {
			duration: enter ? 0.42 : 0.32,
		})
	}

	// all'apertura, e solo allora
	useLayoutEffect(() => {
		const closed = closedState()
		if (!closed) return
		motion.current = run(closed, OPEN, true)
		return () => motion.current?.stop()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Chiudendo, prima si torna indietro e poi si esce
	const leave = then => {
		if (leaving.current) return
		const closed = closedState()
		if (!closed) return then()
		leaving.current = true
		parts.root.current.style.pointerEvents = "none"
		motion.current?.stop()
		run(OPEN, closed, false).finished.then(then)
	}

	const done = () => leave(() => onDone(normalizeSchema({ items })))

	// il tasto indietro del telefono è la ✕: chiude senza cambiare niente
	useBackClose(() => leave(onCancel))

	// ── il campo, con quello che serve al dito ──
	const handle = (id, part, p, filled = false) => (
		<circle
			key={part}
			cx={p.X}
			cy={p.Y}
			r={filled ? 6.5 : 7.5}
			fill={filled ? c.primary : c.schema_line}
			stroke={filled ? c.schema_line : c.primary}
			strokeWidth={filled ? 2 : 2.5}
			data-id={id}
			data-part={part}
		/>
	)

	const handles = []
	const zones = items
		.filter(item => item.type === "zone")
		.map(item => {
			const isSelected = item.id === selectedID
			if (isSelected) {
				const corner = view.toScreen(item.x2, item.y2)
				handles.push(<g key={`h${item.id}`}>{handle(item.id, "corner", corner)}</g>)
			}
			return (
				<g key={item.id}>
					<SchemaZone item={item} view={view} data-id={item.id} data-part="body" style={{ cursor: "move" }} />
					{isSelected && (
						<polygon
							points={polygonPoints(view, zoneCorners(item))}
							fill="none"
							stroke={c.primary}
							strokeWidth="2"
							strokeDasharray="5 4"
							pointerEvents="none"
						/>
					)}
				</g>
			)
		})

	const arrows = items
		.filter(item => item.type === "arrow")
		.map(item => {
			const points = arrowScreenPoints(item, view)
			if (item.id === selectedID) {
				const middle = arrowPoint(item, 0.5)
				handles.push(
					<g key={`h${item.id}`}>
						{handle(item.id, "a", points[0])}
						{handle(item.id, "b", points[points.length - 1])}
						{handle(item.id, "bend", view.toScreen(middle.x, middle.y), true)}
					</g>,
				)
			}
			return (
				<g key={item.id}>
					<SchemaArrow item={item} view={view} />
					{/* una riga sottile non si prende col dito: sopra ce n'è una larga e invisibile */}
					<path
						d={pathOf(points)}
						fill="none"
						stroke="transparent"
						strokeWidth="20"
						data-id={item.id}
						data-part="body"
						style={{ cursor: "move" }}
					/>
				</g>
			)
		})

	const pieces = piecesByDepth(items, view).map(({ item }) => (
		<SchemaPiece key={item.id} item={item} view={view}>
			{item.id === selectedID && (
				<circle cx="0" cy={-pieceLift(item)} r="20" fill="none" stroke={c.primary} strokeWidth="2.2" strokeDasharray="5 4" />
			)}
			<circle
				cx="0"
				cy={-pieceLift(item)}
				r="20"
				fill="transparent"
				data-id={item.id}
				data-part="body"
				style={{ cursor: "move" }}
			/>
		</SchemaPiece>
	))

	// ── le file, mentre si trascina ──
	const along = (from, to) => {
		const a = view.toScreen(from.x, from.y)
		const b = view.toScreen(to.x, to.y)
		return (
			<line
				x1={a.X}
				y1={a.Y}
				x2={b.X}
				y2={b.Y}
				stroke={c.primary}
				strokeWidth="2.5"
				strokeDasharray="7 6"
				strokeLinecap="round"
			/>
		)
	}
	const guideLines = guides && (
		<g pointerEvents="none">
			{guides.x !== null &&
				along({ x: guides.x, y: -FIELD.length / 2 + 0.6 }, { x: guides.x, y: FIELD.length / 2 - 0.2 })}
			{guides.y !== null &&
				along({ x: -FIELD.width / 2 + 0.3, y: guides.y }, { x: FIELD.width / 2 - 0.3, y: guides.y })}
			{[guides.at, ...guides.with].map((point, i) => {
				const p = view.toScreen(point.x, point.y)
				const r = 13 * pieceScale(view, p.k)
				return <ellipse key={i} cx={p.X} cy={p.Y} rx={r} ry={r * 0.42} fill="none" stroke={c.primary} strokeWidth="2.5" />
			})}
		</g>
	)

	// ── "Duplica", sopra quello che si è toccato ──
	// Dove sta sullo schermo il punto più alto della cosa scelta, rispetto al
	// riquadro del campo: lì sopra la pastiglia, o sotto se sopra non c'è posto
	const pillPlace = () => {
		if (!selected) return null
		let top
		let bottom
		if (selected.type === "arrow") {
			const middle = arrowPoint(selected, 0.5)
			const p = view.toScreen(middle.x, middle.y)
			top = { X: p.X, Y: p.Y - 12 }
			bottom = { X: p.X, Y: p.Y + 12 }
		} else if (selected.type === "zone") {
			const a = view.toScreen((selected.x1 + selected.x2) / 2, Math.min(selected.y1, selected.y2))
			const b = view.toScreen((selected.x1 + selected.x2) / 2, Math.max(selected.y1, selected.y2))
			top = { X: a.X, Y: a.Y - 6 }
			bottom = { X: b.X, Y: b.Y + 6 }
		} else {
			const p = view.toScreen(selected.x, selected.y)
			const scale = pieceScale(view, p.k)
			top = { X: p.X, Y: p.Y - (pieceLift(selected) + 22) * scale }
			bottom = { X: p.X, Y: p.Y + 8 * scale }
		}
		const matrix = svgRef.current?.getScreenCTM?.()
		const box = parts.court.current?.getBoundingClientRect()
		if (!matrix || !box) return { left: 0, top: 0, below: false }
		const at = q => ({ x: matrix.a * q.X + matrix.e - box.left, y: matrix.d * q.Y + matrix.f - box.top })
		const above = at(top)
		if (above.y >= 44) return { left: above.x, top: above.y, below: false }
		const under = at(bottom)
		return { left: under.x, top: under.y, below: true }
	}
	const place = !dragging && !isFull && pillPlace()
	const duplicatePill = place && (
		<button
			type="button"
			onClick={duplicate}
			style={{
				position: "absolute",
				left: place.left,
				top: place.top,
				transform: place.below ? "translate(-50%, 0.375rem)" : "translate(-50%, calc(-100% - 0.375rem))",
				height: "2.125rem",
				padding: "0 0.875rem",
				border: 0,
				borderRadius: "17px",
				background: c.surface,
				color: c.gunmetal,
				boxShadow: theme.shadows.lift,
				font: "inherit",
				fontSize: "0.8125rem",
				fontWeight: 700,
				display: "inline-flex",
				alignItems: "center",
				gap: "0.375rem",
				whiteSpace: "nowrap",
				cursor: "pointer",
			}}>
			<FontAwesomeIcon icon={faClone} />
			Duplica
		</button>
	)

	// ── la barra: cosa si può fare con quello che si è toccato ──
	const trash = (
		<button type="button" aria-label="Togli dal campo" onClick={remove} style={{ ...iconButton, marginLeft: "auto" }}>
			<FontAwesomeIcon icon={faTrashCan} />
		</button>
	)
	const hint = text => (
		<span style={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: "0.75rem", color: c.text_light }}>{text}</span>
	)

	// la punta anche alla partenza: un palleggio, o chi va e torna, in una
	// freccia sola
	const doubleArrow = selected?.type === "arrow" && (
		<button
			type="button"
			aria-label="Doppia freccia"
			aria-pressed={selected.both}
			title="Doppia freccia"
			style={{ ...barButton(selected.both), padding: "0 0.5rem" }}
			onClick={() => editSelected({ both: !selected.both })}>
			<svg viewBox="0 0 24 12" width="24" height="12" aria-hidden="true">
				<path d="M5 6h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
				<polygon points="1,6 7,2 7,10" fill="currentColor" />
				<polygon points="23,6 17,2 17,10" fill="currentColor" />
			</svg>
		</button>
	)

	let bar
	if (dragging)
		bar = (
			<>
				<span
					style={{
						flex: 1,
						minWidth: 0,
						display: "inline-flex",
						alignItems: "center",
						gap: "0.375rem",
						fontSize: "0.75rem",
						color: c.text_light,
					}}>
					<FontAwesomeIcon icon={faMagnet} color={snapOn ? c.primary : c.text_light} />
					{!snapOn ? "Va dove va il dito" : guides ? "In fila: si aggancia" : "Si aggancia a righe e pezzi"}
				</span>
				<SegmentedPill
					options={[
						{ value: "on", text: "Guide" },
						{ value: "off", text: "Libero" },
					]}
					selected={snapOn ? "on" : "off"}
					onChange={value => setSnap(value === "on")}
				/>
			</>
		)
	else if (tool) bar = hint("Trascina sul campo, da dove parte a dove arriva")
	else if (!selected)
		bar = (
			<>
				{hint(
					isFull
						? "Il campo è pieno: togli qualcosa per aggiungere"
						: "Tocca un pezzo qui sotto, poi trascinalo dove vuoi",
				)}
				{/* qui dentro il cestino è dello schema, e di nient'altro */}
				{items.length > 0 && (
					<button
						type="button"
						onClick={() => leave(() => onDone(null))}
						style={{
							...barButton(),
							background: c.red_light,
							color: c.red,
							marginLeft: "auto",
						}}>
						<FontAwesomeIcon icon={faTrashCan} />
						Togli lo schema
					</button>
				)}
			</>
		)
	else if (selected.type === "player")
		bar = (
			<>
				{PLAYER_COLORS.map(color => (
					<button
						key={color.id}
						type="button"
						aria-label={color.label}
						aria-pressed={selected.color === color.id}
						onClick={() => editSelected({ color: color.id })}
						style={{
							width: "1.75rem",
							height: "1.75rem",
							flex: "none",
							borderRadius: "50%",
							border: `3px solid ${c.background}`,
							boxShadow: selected.color === color.id ? `0 0 0 2px ${c.gunmetal}` : "none",
							background: c[`schema_${color.id}`],
							cursor: "pointer",
						}}
					/>
				))}
				<span aria-hidden="true" style={{ width: 1, height: "1.25rem", background: c.gray_light, flex: "none" }} />
				<button
					type="button"
					aria-pressed={selected.ghost}
					style={barButton(selected.ghost)}
					onClick={() => editSelected({ ghost: !selected.ghost })}>
					Ombra
				</button>
				<button
					type="button"
					aria-label="Numero della sagoma"
					style={barButton(selected.num > 0)}
					onClick={() => editSelected({ num: (selected.num + 1) % (MAX_NUMBER + 1) })}>
					{selected.num > 0 ? `N. ${selected.num}` : "N."}
				</button>
				{trash}
			</>
		)
	else if (selected.type === "arrow" && selected.kind === "palla")
		bar = (
			<>
				{doubleArrow}
				<SegmentedPill
					options={[
						{ value: "tesa", text: "Tesa" },
						{ value: "tonda", text: "Tonda" },
					]}
					selected={selected.bend ? "tonda" : "tesa"}
					onChange={value => editSelected({ bend: value === "tonda" ? -ROUND_BEND : 0 })}
				/>
				{selected.bend !== 0 && (
					<button type="button" style={barButton()} onClick={() => editSelected({ bend: -selected.bend })}>
						Gira
					</button>
				)}
				{trash}
			</>
		)
	else if (selected.type === "arrow")
		bar = (
			<>
				{doubleArrow}
				{selected.bend !== 0 && (
					<button type="button" style={barButton()} onClick={() => editSelected({ bend: 0 })}>
						Dritta
					</button>
				)}
				{trash}
			</>
		)
	else if (selected.type === "cone")
		bar = (
			<>
				<SegmentedPill
					options={[
						{ value: "disc", text: "Coppella" },
						{ value: "tall", text: "Cono" },
					]}
					selected={selected.tall ? "tall" : "disc"}
					onChange={value => editSelected({ tall: value === "tall" })}
				/>
				{trash}
			</>
		)
	else if (selected.type === "number")
		bar = (
			<>
				<button type="button" aria-label="Numero più basso" style={barButton()} onClick={() => editSelected({ n: Math.max(1, selected.n - 1) })}>
					<FontAwesomeIcon icon={faMinus} />
				</button>
				<span style={{ minWidth: "1.25rem", textAlign: "center", fontWeight: 700 }}>{selected.n}</span>
				<button type="button" aria-label="Numero più alto" style={barButton()} onClick={() => editSelected({ n: Math.min(MAX_NUMBER, selected.n + 1) })}>
					<FontAwesomeIcon icon={faPlus} />
				</button>
				{trash}
			</>
		)
	else
		bar = (
			<>
				<span style={{ fontSize: "0.75rem", color: c.text_light }}>{PIECE_NAMES[selected.type]}</span>
				{trash}
			</>
		)

	return createPortal(
		<div
			ref={parts.root}
			role="dialog"
			aria-modal="true"
			aria-label="Schema dell'esercizio"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 1200,
				color: c.text,
				fontFamily: theme.fontFamily.primary,
				display: "flex",
				flexDirection: "column",
				paddingTop: "env(safe-area-inset-top)",
			}}>
			{/* il campo arriva fino ai bordi: tutto il fondo è il suo blu. Sta a
			    parte perché aprendo si allarga da dov'era lo schema */}
			<div
				ref={parts.bg}
				aria-hidden="true"
				style={{ position: "absolute", inset: 0, zIndex: -1, background: c.schema_court }}
			/>
			<div ref={parts.top}>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "2.25rem 1fr auto",
						alignItems: "center",
						gap: "0.5rem",
						padding: "0.75rem 1rem 0.5rem",
					}}>
					<button
						type="button"
						aria-label="Chiudi senza cambiare lo schema"
						onClick={() => leave(onCancel)}
						style={{ ...iconButton, borderRadius: "50%", background: c.schema_veil, color: c.schema_line }}>
						<FontAwesomeIcon icon={faXmark} />
					</button>
					<div style={{ minWidth: 0, textAlign: "center" }}>
						<div style={{ fontWeight: 700, color: c.schema_line }}>Schema</div>
						<div
							style={{
								fontSize: "0.75rem",
								color: c.schema_line,
								opacity: 0.8,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}>
							{title || "Esercizio senza testo"}
						</div>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
						<button
							type="button"
							aria-label="Annulla l'ultima modifica"
							disabled={history.length === 0}
							onClick={undo}
							style={{ ...iconButton, color: c.schema_line, opacity: history.length === 0 ? 0.35 : 0.9 }}>
							<FontAwesomeIcon icon={faRotateLeft} />
						</button>
						<button
							type="button"
							onClick={done}
							style={{
								height: "2.25rem",
								padding: "0 1rem",
								border: 0,
								borderRadius: "18px",
								background: c.primary,
								color: c.white,
								font: "inherit",
								fontWeight: 600,
								cursor: "pointer",
							}}>
							Fatto
						</button>
					</div>
				</div>

				<div style={{ display: "flex", justifyContent: "center", paddingBottom: "0.5rem" }}>
					{/* la scelta della vista, sul blu: un velo scuro, e la scelta in bianco */}
					<div
						role="group"
						aria-label="Vista"
						style={{ display: "inline-flex", gap: "2px", padding: "3px", borderRadius: "15px", background: c.schema_dim }}>
						{[
							{ value: "persp", text: "In prospettiva" },
							{ value: "top", text: "Dall'alto" },
						].map(option => (
							<button
								key={option.value}
								type="button"
								aria-pressed={mode === option.value}
								onClick={() => setMode(option.value)}
								style={{
									height: "1.875rem",
									padding: "0 0.75rem",
									border: 0,
									borderRadius: "12px",
									background: mode === option.value ? c.schema_line : "transparent",
									color: mode === option.value ? c.schema_coach : c.schema_line,
									opacity: mode === option.value ? 1 : 0.8,
									font: "inherit",
									fontSize: "0.75rem",
									fontWeight: 600,
									whiteSpace: "nowrap",
									cursor: "pointer",
								}}>
								{option.text}
							</button>
						))}
					</div>
				</div>
			</div>

			<div
				ref={parts.court}
				style={{
					position: "relative",
					flex: 1,
					minHeight: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "0 1rem",
				}}>
				<svg
					ref={svgRef}
					viewBox={`0 0 ${view.width} ${view.height.toFixed(1)}`}
					role="img"
					aria-label="Il campo"
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					style={{
						display: "block",
						width: "100%",
						height: "100%",
						touchAction: "none",
						userSelect: "none",
						WebkitUserSelect: "none",
					}}>
					<SchemaCourt view={view} grass={false} />
					{zones}
					{arrows}
					{pieces}
					{handles}
					{guideLines}
				</svg>
				{duplicatePill}
			</div>

			{/* i pezzi su un foglio che sale da sotto, come quelli dell'app */}
			<div
				ref={parts.tray}
				style={{
					background: c.background,
					borderRadius: "26px 26px 0 0",
					paddingTop: "0.5rem",
					paddingBottom: "env(safe-area-inset-bottom)",
				}}>
				<div
					style={{
						minHeight: "3.25rem",
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
						padding: "0.375rem 1rem",
						overflowX: "auto",
					}}>
					{bar}
				</div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(4, 1fr)",
						gap: "0.5rem",
						padding: "0.25rem 1rem 1rem",
					}}>
					{TRAY.map(entry => {
						const isTool = Boolean(entry.tool)
						const pressed = isTool && tool === entry.tool
						return (
							<button
								key={entry.label}
								type="button"
								aria-pressed={isTool ? pressed : undefined}
								disabled={isFull}
								onClick={() => {
									if (isTool) {
										setTool(pressed ? null : entry.tool)
										setSelectedID(null)
									} else add(entry.add)
								}}
								style={{
									height: "3.625rem",
									border: 0,
									borderRadius: "15px",
									background: pressed ? c.primary_light : c.surface,
									boxShadow: pressed ? `inset 0 0 0 2px ${c.primary}` : "none",
									color: pressed ? c.primary : c.gunmetal,
									font: "inherit",
									fontSize: "0.6875rem",
									fontWeight: 600,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									gap: "2px",
									opacity: isFull ? 0.4 : 1,
									cursor: isFull ? "default" : "pointer",
								}}>
								{entry.icon}
								{entry.label}
							</button>
						)
					})}
				</div>
			</div>
		</div>,
		document.body,
	)
}

export default SchemaEditor
